<template>
  <div class="dashboard-contenedor">
    <SlideBar @cambiar-estado="actualizarSidebar" />

    <div class="contenido" :class="{ 'contenido-expandido': sidebarCerrado }">
      <h2 class="titulo">Citas Médicas</h2>

      <!-- Búsqueda -->
      <div class="barra-busqueda">
        <input
          v-model="busqueda"
          type="text"
          placeholder="Buscar por nombre, habitación, tipo…"
          @input="paginaActual = 1"
        >
      </div>

      <!-- Estados -->
      <p v-if="cargando" class="mensaje-cargando">Cargando citas…</p>
      <p v-else-if="citasFiltradas.length === 0" class="mensaje-vacio">No hay citas registradas.</p>

      <!-- Tabla -->
      <div v-else class="tabla-contenedor">
        <div class="tabla-scroll">
        <table class="tabla-citas">
          <thead>
            <tr>
              <th>#</th>
              <th>Nombre Completo</th>
              <th>Edad</th>
              <th>Tipo</th>
              <th>Fecha</th>
              <th>Habitación</th>
              <th>Hora</th>
              <th>Registrado</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="cita in citasPaginadas" :key="cita.id_cita">
              <td class="td-id">{{ cita.id_cita }}</td>
              <td>{{ cita.nombre_completo }}</td>
              <td>{{ cita.edad }} años</td>
              <td>
                <span class="badge-tipo" :class="'tipo-' + cita.tipo_cita">
                  {{ cita.tipo_cita }}
                </span>
              </td>
              <td>{{ formatearFecha(cita.fecha) }}</td>
              <td>{{ cita.habitacion }}</td>
              <td>{{ formatearHora(cita.hora) }}</td>
              <td class="td-meta">{{ formatearCreado(cita.creado_en) }}</td>
            </tr>
          </tbody>
        </table>
        </div><!-- /tabla-scroll -->

        <!-- Paginación -->
        <div class="paginacion">
          <button class="btn-pagina" :disabled="paginaActual === 1" @click="paginaActual--">
            <span class="material-icons">chevron_left</span>
          </button>
          <template v-for="pagina in paginasVisibles" :key="pagina">
            <span v-if="pagina === '...'" class="paginacion-ellipsis">…</span>
            <button
              v-else
              class="btn-pagina"
              :class="{ 'btn-pagina-activa': pagina === paginaActual }"
              @click="paginaActual = pagina"
            >
              {{ pagina }}
            </button>
          </template>
          <button class="btn-pagina" :disabled="paginaActual === totalPaginas" @click="paginaActual++">
            <span class="material-icons">chevron_right</span>
          </button>
          <span class="paginacion-info">
            {{ (paginaActual - 1) * porPagina + 1 }}–{{ Math.min(paginaActual * porPagina, citasFiltradas.length) }}
            de {{ citasFiltradas.length }}
          </span>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import SlideBar from '@/components/SlideBar.vue';
//import Swal from 'sweetalert2';
import DOMPurify from 'dompurify';
import { getConsultas } from '@/utils/api';

export default {
  name: 'CitasMedicas',
  components: { SlideBar },
  data() {
    return {
      sidebarCerrado: false,
      cargando: true,
      busqueda: '',
      citas: [],
      paginaActual: 1,
      porPagina: 10,
    };
  },
  computed: {
    citasFiltradas() {
      if (!this.busqueda.trim()) return this.citas;

      const limpio = DOMPurify.sanitize(this.busqueda)
        .split('')
        .filter(c => c.charCodeAt(0) > 0x1F && c.charCodeAt(0) !== 0x7F)
        .join('')
        .toLowerCase();

      return this.citas.filter(cita =>
        Object.values(cita).some(v => v && v.toString().toLowerCase().includes(limpio))
      );
    },
    totalPaginas() {
      return Math.max(1, Math.ceil(this.citasFiltradas.length / this.porPagina));
    },
    paginasVisibles() {
      const total = this.totalPaginas;
      const actual = this.paginaActual;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const paginas = [];
      paginas.push(1);
      if (actual > 3) paginas.push('...');
      const inicio = Math.max(2, actual - 1);
      const fin = Math.min(total - 1, actual + 1);
      for (let i = inicio; i <= fin; i++) paginas.push(i);
      if (actual < total - 2) paginas.push('...');
      paginas.push(total);
      return paginas;
    },
    citasPaginadas() {
      const inicio = (this.paginaActual - 1) * this.porPagina;
      return this.citasFiltradas.slice(inicio, inicio + this.porPagina);
    },
  },
  methods: {
    actualizarSidebar(estado) {
      this.sidebarCerrado = estado;
    },

    formatearFecha(fecha) {
      if (!fecha || fecha === '0000-00-00') return '—';
      const [y, m, d] = fecha.split('-');
      return `${d}/${m}/${y}`;
    },

    formatearHora(hora) {
      if (!hora) return '—';
      const [h, m] = hora.split(':');
      const hNum = parseInt(h);
      const ampm = hNum >= 12 ? 'PM' : 'AM';
      const h12 = hNum % 12 || 12;
      return `${h12}:${m} ${ampm}`;
    },

    formatearCreado(creado) {
      if (!creado) return '—';
      const [fecha, hora] = creado.split(' ');
      const [y, m, d] = fecha.split('-');
      const [hh, mm] = hora.split(':');
      const hNum = parseInt(hh);
      const ampm = hNum >= 12 ? 'PM' : 'AM';
      const h12 = hNum % 12 || 12;
      return `${d}/${m}/${y} ${h12}:${mm} ${ampm}`;
    },

    async cargarCitas() {
      this.cargando = true;
      const consultas = await getConsultas();
      this.citas = consultas.map(c => ({
        ...c,
        tipo: 'Consulta',
        habitacion: '-',
        fecha_hora: c.fecha ? `${c.fecha}T09:00:00` : '',
      }));
      this.cargando = false;
    },
  },
  mounted() {
    this.cargarCitas();
  },
};
</script>

<style scoped>
/* ============================================================
   DOMÉDICA - CITAS MÉDICAS
   ============================================================ */

/* ---------- CONTENEDOR PRINCIPAL DE CITAS ---------- */
.citas-contenedor {
  display: flex;
  min-height: 100vh;
  background-color: var(--brand-beige); /* Fondo Beige #F4EFEB */
}

/* ---------- LAYOUT ---------- */
.dashboard-contenedor {
  display: flex;
  min-height: 100vh;
  background-color: var(--brand-beige);
}

.contenido {
  flex-grow: 1;
  padding: 24px;
  margin-left: 250px;
  transition: margin-left 0.3s ease;
}

.contenido-expandido { margin-left: 80px; }

/* ---------- TÍTULO ---------- */
.titulo {
  font-size: 26px;
  font-weight: 700;
  color: var(--brand-primary);
  margin-bottom: 20px;
}

/* ---------- BÚSQUEDA ---------- */
.barra-busqueda {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.barra-busqueda input {
  width: 50%;
  padding: 12px 18px;
  border-radius: 10px;
  border: 2px solid var(--brand-secondary-light);
  font-size: 16px;
  text-align: center;
  background: var(--white);
  color: var(--brand-secondary);
  outline: none;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.barra-busqueda input:focus {
  border-color: var(--brand-accent-sage);
  box-shadow: 0 0 0 4px rgba(96, 127, 112, 0.2);
}

.barra-busqueda input::placeholder { color: var(--brand-secondary-light); }

/* ---------- TABLA ---------- */
.tabla-contenedor {
  background: var(--white);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(19, 62, 104, 0.10);
  overflow: hidden;
}

/* scroll horizontal en pantallas pequeñas */
.tabla-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.tabla-citas {
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;
}

.tabla-citas thead {
  background: var(--brand-primary);
  color: var(--white);
}

.tabla-citas th {
  padding: 14px 16px;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.tabla-citas tbody tr {
  border-bottom: 1px solid rgba(19, 62, 104, 0.08);
  transition: background 0.15s ease;
}

.tabla-citas tbody tr:last-child { border-bottom: none; }
.tabla-citas tbody tr:hover { background: rgba(96, 127, 112, 0.06); }

.tabla-citas td {
  padding: 12px 16px;
  font-size: 14px;
  color: var(--brand-secondary);
}

.td-id {
  font-weight: 700;
  color: var(--brand-primary);
  width: 48px;
}

.td-meta {
  font-size: 12px;
  color: var(--brand-secondary-light);
  white-space: nowrap;
}

/* ---------- BADGE TIPO ---------- */
.badge-tipo {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.tipo-normal    { background: rgba(96,127,112,0.15); color: var(--brand-accent-sage); }
.tipo-urgente   { background: rgba(224,49,49,0.12);  color: #c92a2a; }
.tipo-seguimiento { background: rgba(19,62,104,0.12); color: var(--brand-primary); }

/* ---------- PAGINACIÓN ---------- */
.paginacion {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px;
  border-top: 1px solid rgba(19, 62, 104, 0.08);
}

.btn-pagina {
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border: 2px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--brand-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.btn-pagina:hover:not(:disabled) {
  background: rgba(96, 127, 112, 0.12);
  border-color: var(--brand-accent-sage);
  color: var(--brand-accent-sage);
}

.btn-pagina-activa {
  background: var(--brand-accent-sage) !important;
  color: var(--white) !important;
  border-color: var(--brand-accent-sage) !important;
}

.btn-pagina:disabled { opacity: 0.35; cursor: not-allowed; }

.paginacion-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 36px;
  color: var(--brand-secondary-light);
  font-weight: bold;
}

.paginacion-info {
  margin-left: 12px;
  font-size: 13px;
  color: var(--brand-secondary-light);
}

/* ---------- MENSAJES ---------- */
.mensaje-cargando,
.mensaje-vacio {
  color: var(--brand-primary);
  font-weight: 600;
  text-align: center;
  padding: 40px;
  font-size: 16px;
}

/* ---------- RESPONSIVE ---------- */
@media (max-width: 1024px) { .contenido { margin-left: 80px; } }

@media (max-width: 768px) {
  .dashboard-contenedor { flex-direction: column; }

  .contenido {
    margin-left: 0 !important;
    padding: 12px;
    padding-top: 70px;
  }

  .barra-busqueda input { width: 95%; font-size: 14px; }

  .tabla-citas th, .tabla-citas td { padding: 10px 8px; font-size: 12px; }

  .paginacion { flex-wrap: wrap; gap: 4px; padding: 10px 8px; }
  .paginacion-info { width: 100%; text-align: center; margin: 4px 0 0; }
}

@media (max-width: 480px) {
  .contenido { padding: 8px; padding-top: 70px; }
  .barra-busqueda input { width: 100%; font-size: 13px; }
  .titulo { font-size: 20px; }
}
</style>
